import type { ShapeFile, ShapeImportItem, ShapeImportReply, ShapeImportResult } from "./api";

export const MAX_FILES = 50;
export const MAX_FILE_BYTES = 1_000_000;
export const MAX_TOTAL_BYTES = 25_000_000;
export const ACCEPT = ".geojson,.json,application/geo+json,application/json";
export const PREVIEW_COLOUR = "#ff4fd8";

export interface ReadFile {
  name: string;
  size: number;
  text: string;
}

export interface Parsed {
  files: ShapeFile[];
  problems: string[];
}

/** Rule V-20: the GeoJSON of each picked file, and a problem for each file that the API would refuse. */
export function parseShapeFiles(read: ReadFile[]): Parsed {
  const problems: string[] = [];
  if (read.length > MAX_FILES) return { files: [], problems: [`Pick at most ${MAX_FILES} files. You picked ${read.length}.`] };
  const total = read.reduce((sum, file) => sum + file.size, 0);
  if (total > MAX_TOTAL_BYTES) return { files: [], problems: ["The files are larger than 25 MB together. Pick fewer files."] };
  const files: ShapeFile[] = [];
  for (const file of read) {
    if (file.size > MAX_FILE_BYTES) {
      problems.push(`${file.name}: the file is larger than 1 MB.`);
      continue;
    }
    try {
      files.push({ name: file.name, geojson: JSON.parse(file.text) });
    } catch {
      problems.push(`${file.name}: the file is not JSON. Export the shape again as GeoJSON.`);
    }
  }
  return { files, problems };
}

export const RESULT_TEXT: Record<ShapeImportResult, string> = {
  ok: "New shape",
  colour: "New colour only",
  unchanged: "No change",
  no_area: "No area with this code",
  bad_shape: "Bad shape",
  not_allowed: "Your role cannot change it",
};

/** The items that an apply writes. */
export const toWrite = (items: ShapeImportItem[]) => items.filter((item) => item.result === "ok" || item.result === "colour");

/** The text of the corners column: "14", or "1,500 → 498" when the import simplified the shape. */
export function cornersText(item: ShapeImportItem): string {
  if (!item.corners) return item.corners_in ? item.corners_in.toLocaleString("en-US") : "—";
  if (item.corners_in > item.corners) return `${item.corners_in.toLocaleString("en-US")} → ${item.corners.toLocaleString("en-US")}`;
  return item.corners.toLocaleString("en-US");
}

const plural = (count: number, one: string, many = `${one}s`) => `${count.toLocaleString("en-US")} ${count === 1 ? one : many}`;

/** One line about the dry run: what an apply changes. */
export function checkText(reply: ShapeImportReply): string {
  const shapes = reply.counts.ok ?? 0;
  const colours = reply.counts.colour ?? 0;
  const left = reply.items.length - shapes - colours - (reply.counts.unchanged ?? 0);
  const parts = [`${plural(shapes, "new shape")}`];
  if (colours) parts.push(plural(colours, "new colour"));
  if (reply.counts.unchanged) parts.push(`${plural(reply.counts.unchanged, "area")} with no change`);
  if (left) parts.push(`${plural(left, "item")} not imported`);
  const assign = reply.would_assign ? ` The new shapes give ${plural(reply.would_assign, "account")} with no plan an area.` : "";
  return `${parts.join(", ")}.${assign}`;
}

/** The accounts that the apply left with no area: a pin in no shape, or no pin. */
export const openAccounts = (reply: ShapeImportReply) => (reply.assign?.no_shape ?? 0) + (reply.assign?.no_pin ?? 0);

/** The account list of the accounts with no service plan, where a person gives each one an area. */
export const NO_PLAN_LINK = "/accounts?noplan=1";

/** One line about the apply: the shapes and colours kept, and the accounts that got an area. */
export function appliedText(reply: ShapeImportReply): string {
  const written = reply.written ?? { shapes: 0, colours: 0 };
  const parts = [`Saved ${plural(written.shapes, "shape")}`];
  if (written.colours) parts.push(plural(written.colours, "colour"));
  const kept = written.shapes + (written.colours ?? 0) === 1 ? "it" : "them";
  let text = `${parts.join(" and ")}. The night job keeps ${kept}.`;
  const assign = reply.assign;
  if (assign) {
    text += ` ${plural(assign.assigned ?? 0, "account")} with no plan got the area of the pin`;
    const open = openAccounts(reply);
    text += open ? `; ${plural(open, "account")} ${open === 1 ? "has" : "have"} no area yet.` : ".";
  }
  return text;
}
